import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetLanguagesQuery } from "@/graphql/globalVar/globalVar.hooks";
import LanguageSelect from "@/components/ui/inputs/languageSelect";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { normalizeLocaleCode } from "@/utils/languageVersions/localeHelpers/normalizeLocaleCode";
import {
  resolveMenuLocale,
  resolveUiLocale,
} from "@/utils/languageVersions/localeHelpers/resolveLocales";
import {
  buildPathWithLocale,
  stripLocalePrefix,
} from "@/utils/languageVersions/localeHelpers/pathWithLocale";
import { getMenuTypeFromUrl } from "@/components/cart/helpers/cartEvents";

interface ILanguageSwitcherProps {
  wrapperClass?: string;
  view: "mobile" | "desktop";
}

const LanguageSelectWrapper: React.FC<ILanguageSwitcherProps> = ({
  wrapperClass,
  view,
}) => {
  const {
    locale: localeFromParams,
    menuId,
    branchId,
    typeMenu,
  } = useParams<{
    locale?: string;
    menuId?: string;
    branchId?: string;
    typeMenu?: string;
  }>();
  const menuTypeEnum = getMenuTypeFromUrl(typeMenu);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const hasMenuId = Boolean(menuId);

  const { data, loading } = useGetLanguagesQuery({
    variables: {
      menuId: Number(menuId),
      branchId: Number(branchId),
      typeMenu: hasMenuId ? null : menuTypeEnum,
    },
    skip: !branchId || !menuTypeEnum,
    fetchPolicy: "cache-first",
  });

  const menuData = data?.menu;

  const availableLocales = useMemo(() => {
    return menuData?.languages?.map((language) => language.isoCode) ?? [];
  }, [menuData?.languages]);

  const primaryLocale = menuData?.primaryLanguage?.isoCode ?? "en";

  const isLanguagesReady = Boolean(menuData && branchId);

  const menuLocale = isLanguagesReady
    ? resolveMenuLocale(localeFromParams, availableLocales, primaryLocale)
    : "";

  const restPath = stripLocalePrefix(pathname, localeFromParams);
  const targetPath = isLanguagesReady
    ? buildPathWithLocale(normalizeLocaleCode(menuLocale), restPath)
    : pathname;

  useEffect(() => {
    if (!isLanguagesReady) {
      return;
    }
    if (targetPath !== pathname) {
      navigate(targetPath, { replace: true });
    }
  }, [isLanguagesReady, navigate, pathname, targetPath]);

  const uiLocale = isLanguagesReady ? resolveUiLocale(menuLocale) : "en";

  useEffect(() => {
    if (!isLanguagesReady) {
      return;
    }

    const currentUiLocale = normalizeLocaleCode(i18n.language);

    if (currentUiLocale !== uiLocale) {
      void i18n.changeLanguage(uiLocale);
    }
  }, [i18n, isLanguagesReady, uiLocale]);

  const handleLanguageChange = useCallback(
    (nextSelectedIsoCode: string) => {
      if (!isLanguagesReady) {
        return;
      }

      const nextPath = buildPathWithLocale(
        normalizeLocaleCode(nextSelectedIsoCode),
        restPath
      );

      navigate(nextPath);
    },
    [isLanguagesReady, navigate, restPath]
  );

  const options = useMemo(() => {
    const availableLanguageEntries = menuData?.languages ?? [];

    return availableLanguageEntries.map((language) => ({
      value: language.isoCode,
      label: language.isoCode.toUpperCase(),
    }));
  }, [menuData?.languages]);

  return (
    <LanguageSelect
      view={view}
      wrapperClass={wrapperClass}
      value={menuLocale}
      options={options}
      isLoading={loading}
      onChange={handleLanguageChange}
    />
  );
};

export default LanguageSelectWrapper;
